import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FetchResult } from '@apollo/client/core';
import { FetchPolicy } from '@core/enums/fetch-policy-apollo.enum';
import {
  ArchiveResponseCourierSchedule,
  CourierScheduleResponse,
  CourierWorkSchedulesFilter,
} from '@core/interfaces/courier-shedule.interface';
import { ILocationFilter } from '@core/interfaces/time-window.interface';
import { DateRange } from '@core/interfaces/date-filter.interface';
import { Apollo, MutationResult, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { environment } from '@environments/environment';
import { UpdateCourierWorkSchedulesDto } from '@core/dtos/update-courier-work-schedule.dto';
import { GetOrdersThatDontFitInVehicleDto } from '@core/dtos/get-orders-that-dont-fit-for-route-list.dto';
import { Order } from '@core/interfaces/order.interface';
import { Courier } from '@core/interfaces/courier-directory.interface';
import { CreateCourierWorkSchedulesDto } from '@core/dtos/create-courier-work-schedules.dto';
import { CreateTransitCourierWorkSchedulesDto } from '@core/dtos/create-transit-courier-work-schedules.dto';
import { SwapCourierInWorkSchedulesDto } from '@core/dtos/swap-courier-in-work-schedules.dto';

@Injectable({
  providedIn: 'root',
})
export class CourierScheduleService {
  private readonly apollo = inject(Apollo);

  private readonly http = inject(HttpClient);

  public exportZoneStats(dateRange: DateRange, locationFilter: ILocationFilter): Observable<Blob> {
    let params = new HttpParams();

    if (dateRange.fromDate) {
      params = params.set('dateRange[from]', dayjs(dateRange.fromDate).toISOString());
    }
    if (dateRange.toDate) {
      params = params.set('dateRange[to]', dayjs(dateRange.toDate).toISOString());
    }

    if (!isNil(locationFilter.zoneId)) {
      params = params.set('zoneId', locationFilter.zoneId.toString());
    }
    if (!isNil(locationFilter.cityId)) {
      params = params.set('cityId', locationFilter.cityId.toString());
    }
    if (!isNil(locationFilter.regionDistrictId)) {
      params = params.set('regionDistrictId', locationFilter.regionDistrictId.toString());
    }
    if (!isNil(locationFilter.regionId)) {
      params = params.set('regionId', locationFilter.regionId.toString());
    }
    if (!isNil(locationFilter.countryId)) {
      params = params.set('countryId', locationFilter.countryId.toString());
    }

    return this.http.get(`${environment.serverUrl}zone-stats/export`, {
      params,
      responseType: 'blob',
    });
  }

  public getCourierSchedule(
    filter: CourierWorkSchedulesFilter,
  ): Observable<
    MutationResult<{ getListCourierWorkSchedule: Array<Courier & { workSchedules: CourierScheduleResponse[] }> }>
  > {
    return this.apollo.query<{
      getListCourierWorkSchedule: Array<Courier & { workSchedules: CourierScheduleResponse[] }>;
    }>({
      query: gql`
        query getListCourierWorkSchedule($filter: GetCourierWorkSchedulesFilter!) {
          getListCourierWorkSchedule(filter: $filter) {
            id
            courierType
            name
            workSchedules {
              id
              courierId
              timeFrom
              timeTo
              shiftDate
              status
              archivedAt
              canArchive
              canUpdate
              reason
              zones {
                id
                color
              }
              vehicle {
                id
                name
                type {
                  type
                }
              }
              routeTemplate {
                id
                name
                archivedAt
              }
              type
            }
          }
        }
      `,
      variables: {
        filter,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getOrdersThatDontFitForRouteList(
    params: GetOrdersThatDontFitInVehicleDto,
  ): Observable<MutationResult<{ getOrdersThatDoNotFitForRouteList: Order[] }>> {
    return this.apollo.query<{ getOrdersThatDoNotFitForRouteList: Order[] }>({
      query: gql`
        query getOrdersThatDoNotFitForRouteList($params: GetOrdersThatDontFitInVehicleDto!) {
          getOrdersThatDoNotFitForRouteList(data: $params) {
            id
          }
        }
      `,
      variables: {
        params,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public createZoneCourierWorkSchedules(
    data: CreateCourierWorkSchedulesDto,
  ): Observable<MutationResult<{ createZoneCourierWorkSchedules: CourierScheduleResponse }>> {
    return this.apollo.mutate<{ createZoneCourierWorkSchedules: CourierScheduleResponse }>({
      mutation: gql`
        mutation createZoneCourierWorkSchedules($data: CreateCourierWorkSchedulesDto!) {
          createZoneCourierWorkSchedules(data: $data) {
            id
          }
        }
      `,
      variables: {
        data,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public createExpressCourierWorkSchedules(
    data: CreateCourierWorkSchedulesDto,
  ): Observable<MutationResult<{ createExpressCourierWorkSchedules: CourierScheduleResponse }>> {
    return this.apollo.mutate<{ createExpressCourierWorkSchedules: CourierScheduleResponse }>({
      mutation: gql`
        mutation createExpressCourierWorkSchedules($data: CreateCourierWorkSchedulesDto!) {
          createExpressCourierWorkSchedules(data: $data) {
            id
          }
        }
      `,
      variables: {
        data,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public createTransitCourierWorkSchedules(
    data: CreateTransitCourierWorkSchedulesDto,
  ): Observable<MutationResult<{ createTransitCourierWorkSchedules: CourierScheduleResponse }>> {
    return this.apollo.mutate<{ createTransitCourierWorkSchedules: CourierScheduleResponse }>({
      mutation: gql`
        mutation createTransitCourierWorkSchedules($data: CreateTransitCourierWorkSchedulesDto!) {
          createTransitCourierWorkSchedules(data: $data) {
            id
          }
        }
      `,
      variables: {
        data,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public updateCourierWorkSchedule(
    id: number,
    data: UpdateCourierWorkSchedulesDto,
  ): Observable<MutationResult<{ updateWorkSchedule: CourierScheduleResponse }>> {
    return this.apollo.mutate<{ updateWorkSchedule: CourierScheduleResponse }>({
      mutation: gql`
        mutation updateWorkSchedule($id: Int!, $data: UpdateCourierWorkSchedulesDto!) {
          updateWorkSchedule(id: $id, data: $data) {
            id
          }
        }
      `,
      variables: {
        id,
        data,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public archiveCourierWorkSchedule(
    id: number,
  ): Observable<FetchResult<{ archiveCourierWorkSchedule: ArchiveResponseCourierSchedule }>> {
    return this.apollo.mutate<{ archiveCourierWorkSchedule: ArchiveResponseCourierSchedule }>({
      mutation: gql`
        mutation archiveCourierWorkSchedule($id: Float!) {
          archiveCourierWorkSchedule(id: $id) {
            id
            archivedAt
          }
        }
      `,
      variables: {
        id,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public swapCourierInWorkSchedules(
    data: SwapCourierInWorkSchedulesDto,
  ): Observable<FetchResult<{ swapCourierInWorkSchedules: CourierScheduleResponse[] }>> {
    return this.apollo.mutate<{ swapCourierInWorkSchedules: CourierScheduleResponse[] }>({
      mutation: gql`
        mutation swapCourierInWorkSchedules($data: SwapCourierInWorkSchedulesDto!) {
          swapCourierInWorkSchedules(data: $data) {
            id
            courierId
          }
        }
      `,
      variables: {
        data,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }
}
